import _extends from '@babel/runtime/helpers/extends';
import PT from 'prop-types';
import React__default, { createContext, useContext } from 'react';
import { g as getDataTaktAttributesFunction } from './TaktIdContext.utils-12ae0974.js';

const DEFAULT_DELIMITER = ':';
const TaktIdContext = /*#__PURE__*/createContext({
  taktFeatureChain: undefined,
  delimiter: undefined,
  useFallbacks: true
});

// ---------------------------------------------

const TaktIdProvider = ({
  children,
  taktId,
  customDelimiter_DO_NOT_USE,
  // eslint-disable-line camelcase
  useFallbacks,
  fallbackId
}) => {
  var _ref, _context$delimiter;
  const context = useContext(TaktIdContext);
  /**
   * if this is the top of the feature chain, and no taktId was provided,
   * disregard this provider
   */
  if (!context.taktFeatureChain && !taktId) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }
  /**
   * use the top level delimiter if set,
   * else we're at the top level and use the one passed in,
   * else fallback to default ':'
   */
  const delim = (_ref = (_context$delimiter = context.delimiter) !== null && _context$delimiter !== void 0 ? _context$delimiter : customDelimiter_DO_NOT_USE) !== null && _ref !== void 0 ? _ref : DEFAULT_DELIMITER; // eslint-disable-line camelcase, max-len
  // set whether fallbacks should be used based on having an existing context & useFallbacks flag
  const canUseFallbacks = context.taktFeatureChain ? context.useFallbacks : useFallbacks;
  // determine which Id to append to the chain
  const appendedId = taktId || (canUseFallbacks ? fallbackId : undefined);
  // if no id was provided that can be used, do not append anything with this provider
  if (!appendedId) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children);
  }

  // if previous prefix exists, append to it, else use passed in prefix to start context chain
  const appendedChain = context.taktFeatureChain ? `${context.taktFeatureChain}${delim}${appendedId}` : appendedId;
  return /*#__PURE__*/React__default.createElement(TaktIdContext.Provider, {
    value: {
      taktFeatureChain: appendedChain,
      delimiter: delim,
      useFallbacks: canUseFallbacks
    }
  }, children);
};
TaktIdProvider.propTypes = {
  taktId: PT.string,
  customDelimiter_DO_NOT_USE: PT.string,
  useFallbacks: PT.bool,
  fallbackId: PT.string
};
TaktIdProvider.defaultProps = {
  taktId: undefined,
  customDelimiter_DO_NOT_USE: undefined,
  useFallbacks: true,
  fallbackId: undefined
};

// --------------------------------------------

const getTaktIdParams = ({
  taktIdContext,
  taktId,
  fallbackId
}) => {
  // determine the taktId to append to use
  // if there is no parent chain, do not append anything or return anything
  const hasParentChain = taktIdContext.taktFeatureChain;
  const dataTaktId = hasParentChain ? taktId || (taktIdContext.useFallbacks ? fallbackId : undefined) : taktId;
  return {
    dataTaktId,
    dataTaktFeature: taktIdContext.taktFeatureChain,
    getDataTaktAttributes: getDataTaktAttributesFunction({
      taktId: dataTaktId,
      taktIdContext
    })
  };
};

/**
 * TaktIdContext consumer that vends a function: (contextValue) => (Children)
 */
const TaktIdConsumer = ({
  children,
  taktId,
  fallbackId
}) => /*#__PURE__*/React__default.createElement(TaktIdContext.Consumer, null, taktIdContext => {
  const {
    dataTaktId,
    dataTaktFeature,
    getDataTaktAttributes
  } = getTaktIdParams({
    taktIdContext,
    taktId,
    fallbackId
  });

  // if there is no parent chain, do not append anything or return anything
  if (!dataTaktFeature) {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, children({
      dataTaktId,
      getDataTaktAttributes
    }));
  }
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: dataTaktId
  }, children({
    dataTaktId,
    dataTaktFeature,
    getDataTaktAttributes
  }));
});
TaktIdConsumer.propTypes = {
  taktId: PT.string,
  fallbackId: PT.string
};
TaktIdConsumer.defaultProps = {
  taktId: undefined,
  fallbackId: undefined
};

/**
 * React hook to append an taktId to the context
 * without modifying the existing context value.
 *
 * If no context exists, this will return undefined.
 *
 * @param taktId: string - value returned as id if context exists
 * @param fallbackId: string - value returned as id if taktId is undefined and useFallbacks is true
 *
 * @returns params: TaktIdParams - object containing:
 *   - dataTaktId?: string - taktId value to be used. May be `undefined` if no context existed above.
 *   - dataTaktFeature?: string - delimited chain of Ids from all TaktIdProviders & TaktIdConsumers
 *     above this hook call in the hierarchy
 *   - getDataTaktAttributes: GetDataTaktAttributes
 */
const useTaktId = (taktId, fallbackId) => {
  const taktIdContext = useContext(TaktIdContext);
  return getTaktIdParams({
    taktIdContext,
    taktId,
    fallbackId
  });
};

/**
 * For non-exported components already have taktId prop but need a new fallback id
 * This should only be used on non-exported components, otherwise we expose the fallbackId prop.
 */
function withTaktFallbackId(Component) {
  const ComponentWithTaktFallbackId = props => {
    const {
      taktId,
      taktFallbackId,
      ...restProps
    } = props;
    const {
      dataTaktId
    } = useTaktId(taktId, taktFallbackId);

    // https://github.com/Microsoft/TypeScript/issues/28938#issuecomment-450636046
    return /*#__PURE__*/React__default.createElement(Component, _extends({
      taktId: dataTaktId
    }, restProps));
  };
  return ComponentWithTaktFallbackId;
}

export { TaktIdProvider as T, TaktIdConsumer as a, TaktIdContext as b, useTaktId as u, withTaktFallbackId as w };
//# sourceMappingURL=TaktIdContext-189dc982.js.map
